import React, { useContext, useState } from 'react';
import Modal, { Footer } from 'components/common/Modal';
import BuilderContext from '@/components/common/Context';
import { Form } from 'react-final-form';
import VocabDropdown from './VocabDropdown';
import { sortedDraggableChoicesByPosition } from '../DraggableChoiceEditor/Utils';

interface LinkVocabularyModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

const LinkVocabularyModal = ({ isOpen, closeModal }: LinkVocabularyModalProps) => {
  const [updatedChoices, setUpdatedChoices] = useState([]);

  // @ts-ignore
  const {
    correctAnswerDispatch,
    dragAndDropSettingsDispatch,
    draggableChoiceDispatch,
    draggableChoices,
    dropZoneDispatch,
    textAreaDispatch,
    dropZoneShape,
    vocabLinkOption,
    setVocabLinkOption,
  } = useContext(BuilderContext);

  const sortedDraggableChoices = sortedDraggableChoicesByPosition(draggableChoices);

  const onSubmit = async () => {
    await draggableChoiceDispatch({ payload: updatedChoices, type: 'UPDATE_VOCAB' });
    await dropZoneDispatch({
      payload: updatedChoices,
      type: 'UPDATE_VOCAB',
      textAreaDispatch,
      shape: dropZoneShape,
      linkOption: vocabLinkOption,
    });
    await correctAnswerDispatch({ payload: updatedChoices, type: 'UPDATE_VOCAB' });
    await textAreaDispatch({
      payload: { linkOption: vocabLinkOption, shape: dropZoneShape },
      type: 'SPACE_TEXT_AREAS',
    });
    await dropZoneDispatch({ linkOption: vocabLinkOption, type: 'MATCH_DROPZONES_TO_TEXT_AREAS' });
    closeModal();
  };

  const handleLinkChange = (linkOption) => {
    dragAndDropSettingsDispatch({ payload: linkOption[0], type: 'SET_VOCAB_LINK_OPTION' });
    setVocabLinkOption(linkOption[0]);
  };

  const validateForm = () => {
    if (vocabLinkOption === 'definition') {
      return updatedChoices.length === 0;
    }

    return updatedChoices.length !== Object.keys(draggableChoices).length;
  };

  return (
    <Modal
      nearlyFullScreen
      closeModal={closeModal}
      headerText="Link Vocabulary"
      isOpen={isOpen}
    >
      <Form
        mutators={{ handleLinkChange }}
        render={({ handleSubmit, form }) => (
          <form className="tw-text-base" onSubmit={handleSubmit}>
            <div className="tw-flex tw-flex-col">
              <div>
                Select the terms you want to link to answer choices.
              </div>
              <div className="tw-my-2 tw-flex tw-gap-x-8">
                <span className="tw-flex tw-items-center tw-justify-center">
                  <input
                    checked={vocabLinkOption === 'definition'}
                    id="definition"
                    name="link_with"
                    type="radio"
                    value="definition"
                    onChange={() => form.mutators.handleLinkChange('definition')}
                  />
                  <label htmlFor="definition">Link terms with definitions</label>
                </span>
                <span className="tw-flex tw-items-center tw-justify-center">
                  <input
                    checked={vocabLinkOption === 'images'}
                    id="images"
                    name="link_with"
                    type="radio"
                    value="images"
                    onChange={() => form.mutators.handleLinkChange('images')}
                  />
                  <label htmlFor="images">Link terms with images</label>
                </span>
              </div>
            </div>
            <div className="tw-ml-2 tw-h-[87.5%]">
              {sortedDraggableChoices.map(([key, draggableChoice], index) => (
                <VocabDropdown
                  {...draggableChoice}
                  key={key}
                  displayPosition={index + 1}
                  id={key}
                  selectedChoices={updatedChoices}
                  setUpdatedChoices={setUpdatedChoices}
                  vocabLinkOption={vocabLinkOption}
                />
              ))}
              <button
                className="btn btn--sm btn--outline-purple tw-mt-4 tw-cursor-pointer"
                type="button"
                onClick={() => {
                  draggableChoiceDispatch({ type: 'ADD' });
                }}
              >
                Add Choice
              </button>
            </div>
            <Footer
              primaryButtonDisabled={validateForm()}
              primaryButtonText="Save"
              secondaryButtonCallback={closeModal}
            />
          </form>
        )}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default LinkVocabularyModal;
